import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const AddEditDoctorModal = ({
  doctor,
  clinics,
  onClose,
  onSave,
  isSubmitting,
}) => {
  const [name, setName] = useState("");
  const [clinic, setClinic] = useState("");
  const [errors, setErrors] = useState({});
  const isEdit = Boolean(doctor);

  useEffect(() => {
    setName(doctor?.name || "");
    setClinic(doctor?.clinic || "");
    setErrors({});
  }, [doctor]);

  const handleSubmit = () => {
    const nextErrors = {};
    const trimmedName = name.trim();
    const trimmedClinic = clinic.trim();

    if (!trimmedName) {
      nextErrors.name = "Doctor name is required";
    }
    if (!trimmedClinic) {
      nextErrors.clinic = "Assigned clinic is required";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSave({ name: trimmedName, clinic: trimmedClinic });
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 1070,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          width: "520px",
          maxWidth: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <div
          style={{
            padding: "20px 24px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: "#111111",
            }}
          >
            {isEdit ? "Edit Doctor" : "Add Doctor"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: "#FFFFFF",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              color: "#333333",
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: 500,
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          <div
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <label
              htmlFor="doctor-name"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#333333",
                marginBottom: "10px",
              }}
            >
              Doctor Name
            </label>
            <input
              id="doctor-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
              }}
              placeholder="Enter Doctor Name"
              disabled={isSubmitting}
              style={{
                width: "100%",
                backgroundColor: "#FFFFFF",
                border: errors.name ? "1px solid #EF4444" : "1px solid #E5E7EB",
                borderRadius: "8px",
                height: "48px",
                padding: "0 16px",
                fontSize: "14px",
                color: "#444444",
                outline: "none",
                marginBottom: "16px",
              }}
            />
            {errors.name && (
              <p
                style={{
                  color: "#EF4444",
                  fontSize: "13px",
                  marginTop: "-12px",
                  marginBottom: "12px",
                }}
              >
                {errors.name}
              </p>
            )}

            <label
              htmlFor="doctor-clinic"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#333333",
                marginBottom: "10px",
              }}
            >
              Assigned Clinic
            </label>
            <select
              id="doctor-clinic"
              value={clinic}
              onChange={(e) => {
                setClinic(e.target.value);
                if (errors.clinic)
                  setErrors((prev) => ({ ...prev, clinic: "" }));
              }}
              disabled={isSubmitting}
              style={{
                width: "100%",
                backgroundColor: "#FFFFFF",
                border: errors.clinic
                  ? "1px solid #EF4444"
                  : "1px solid #E5E7EB",
                borderRadius: "8px",
                height: "48px",
                padding: "0 16px",
                fontSize: "14px",
                color: clinic ? "#444444" : "#9CA3AF",
                outline: "none",
              }}
            >
              <option value="">Select Clinic</option>
              {clinics.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
              {clinic && !clinics.some((c) => c.name === clinic) && (
                <option value={clinic}>{clinic}</option>
              )}
            </select>
            {errors.clinic && (
              <p
                style={{ color: "#EF4444", fontSize: "13px", marginTop: "8px" }}
              >
                {errors.clinic}
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                color: "#333333",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                flex: 1,
                backgroundColor: "#27B99A",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Add Doctor"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddEditDoctorModal;
